<script setup>
import { computed } from 'vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

// Attrs (class/style from the caller) go on the chip, not on the renderless v-tooltip root.
defineOptions({ inheritAttrs: false });

const props = defineProps({
    tag: { type: String, required: true },
    /** Icon-only Early access chip for tight spots like the 80px nav rail. */
    compact: { type: Boolean, default: false },
    /**
     * Override the session claims: 'preview' | 'new' | null. The Feature flags page passes the
     * state derived from the row itself so a live flip shows immediately instead of at next login.
     */
    state: { type: String, default: undefined },
});

const { isNew, isPreview } = useFeatureFlags();
const previewTooltip = "Early access — we'd love your feedback. Let us know if anything doesn't work as expected.";

// Granted but not live: only some accounts have this. Live and inside the New window: plain New.
const marker = computed(() => {
    if (props.state !== undefined) return props.state;
    if (isPreview(props.tag)) return 'preview';
    return isNew(props.tag) ? 'new' : null;
});
</script>

<template>
    <v-tooltip v-if="marker === 'preview'" :text="previewTooltip" location="right">
        <template #activator="{ props: tooltipProps }">
            <v-chip v-bind="{ ...tooltipProps, ...$attrs }" color="warning" :class="compact ? 'px-1' : 'ml-2'" data-cy="featurePreviewIndicator">
                <font-awesome-icon :icon="['fas', 'flag']" :class="{ 'mr-1': !compact }" />
                <template v-if="!compact">Early access</template>
            </v-chip>
        </template>
    </v-tooltip>
    <!-- Same pulsing superscript as the Run eligibility check button; a chip covered the rail icon. -->
    <sup v-else-if="marker === 'new'" v-bind="$attrs" class="bn-feature-flag" data-cy="featureNewIndicator">NEW</sup>
</template>
